import React from 'react';
import ChartCard from '@ui/chart/ChartCard';
import DataTable, { DataTableColumn, DataTableRow } from '@ui/chart/DataTable';
import BarChart from '@ui/chart/BarChart';
import { BiProperty, RevenueScope, RoomTypeBreakdownRow, RoomTypeReport, RoomTypeRow, VatBasis } from '@app/model/bi/Bi';
import { formatMonthRange, MonthRange } from '@ui/selector/MonthRangePicker';
import { scopeRulesNote } from '@app/model/integration-settings/ReportRules';
import { formatDate, heldBlocksNote, missingNote, missingOf, occupiedOf, revenueContent, revenueName, shortName } from '../Bi.utilities';
import {
	adr, allCells, allColumns, ColumnSet, hasNightsToday, occupancyWords, propertyOccupancy, revenue, revenueBar, sortedRows, today, transposedMeasures, typeOccupancy,
} from './RoomTypes.utilities';

type State = 'default' | 'loading' | 'refreshing' | 'error';

const periodText = (report: RoomTypeReport) =>
	`Stay dates ${formatDate(report.stayFrom)} – ${formatDate(report.stayTo)} · past nights actual, later nights on the books`;

const figuresOfReport = (report: RoomTypeReport) =>
	[...report.rows.flatMap(r => [today(r), r.stly, r.lyActual]), today(report.total)];

// The id of a row: "No room type" has the empty key.
const rowId = (r: RoomTypeRow) => r.key === '' ? '__none' : r.key;

// The channels and the boards of a room type (RQ-25) open under its row, with the same columns: Res., RN, Revenue, ADR
// and the share of the type's revenue. The channels come first, then the boards in gray; the names carry no prefix, so
// the first column stays narrow.
const breakdownRows = (row: RoomTypeRow, scope: RevenueScope, vat: VatBasis): DataTableRow[] => {
	const typeRevenue = revenue(today(row), scope, vat);
	const child = (kind: 'channel' | 'board', b: RoomTypeBreakdownRow): DataTableRow => {
		const rev = revenue(b, scope, vat);
		return {
			id: `${row.key}-${kind}-${b.name}`, muted: kind === 'board',
			cells: { title: b.name, rn: b.roomNights, rev: rev === null ? null : Math.round(rev), adr: adr(rev, occupiedOf(b)), res: b.reservations,
				share: rev === null || typeRevenue === null || typeRevenue === 0 ? null : rev / typeRevenue },
		};
	};
	const byRevenue = (a: RoomTypeBreakdownRow, b: RoomTypeBreakdownRow) => (revenue(b, scope, vat) ?? 0) - (revenue(a, scope, vat) ?? 0);
	return [...[...row.channels].sort(byRevenue).map(b => child('channel', b)), ...[...row.boards].sort(byRevenue).map(b => child('board', b))];
};

type AllProps = {
	report: RoomTypeReport | undefined,
	period: MonthRange,
	columnSets: ColumnSet[],
	transposed: boolean,
	scope: RevenueScope,
	vat: VatBasis,
	asOf: string,
	// The names of the properties without last-year stays (decision 20), for the note.
	missingNames: string[],
	// ROO2-315 part c1: the properties of the selection, for the note of the report rules.
	properties: number,
	// ROO2-302 (Q1 C): the note that the held rooms of group blocks are not in this report yet (heldBlocksNote); null without them.
	heldNote?: string | null,
	state: State,
	onRetry: () => void,
	actions?: React.ReactNode,
};

// The All table (RQ-21 … RQ-26): the revenue bar, then one row per room type (or, Transposed, one column per room type),
// and the TOTAL row. A row opens to its channels and boards.
export const RoomTypeAllCard: React.FunctionComponent<AllProps> = ({ report, period, columnSets, transposed, scope, vat, asOf, missingNames, properties, heldNote = null, state, onRetry, actions }) => {
	const rows = report === undefined ? [] : sortedRows(report.rows, scope, vat);
	// ROO2-271 D1: with a property that has no last-year stays, the table shows today like-for-like.
	const lfl = missingNames.length > 0;
	const measures = transposedMeasures(lfl);
	const { columns, groups } = allColumns(columnSets, lfl);
	const note = report === undefined ? null : missingNote(missingOf(figuresOfReport(report), scope, vat), '–', 'the figure');
	// ROO2-315 part c1 (Q4): the report rules, once, for a selection with rules.
	const rulesNote = report === undefined ? null : scopeRulesNote(report.reportRules, report.rulesDiffer, properties);
	const empty = report !== undefined && rows.length === 0;
	const tableRows: DataTableRow[] = report === undefined ? [] : rows.map(r => ({
		id: rowId(r), cells: allCells(r, report.total, scope, vat), muted: r.kind === 'NO_ROOM_TYPE' || today(r).roomNights === 0,
		note: r.properties > 1 ? `· ${r.properties} properties` : undefined,
		children: r.channels.length + r.boards.length > 0 ? breakdownRows(r, scope, vat) : undefined,
	}));
	const transposedColumns: DataTableColumn[] = [{ id: 'measure', label: 'Measure' }, ...rows.map(r => ({ id: rowId(r), label: r.title, sortable: false })),
		{ id: '__total', label: 'Total', sortable: false }];
	const transposedRows: DataTableRow[] = report === undefined ? [] : measures.map(m => {
		const format = (v: unknown) => v === null || v === undefined ? null : v as number;
		const cells: DataTableRow['cells'] = { measure: m.label };
		rows.forEach(r => { cells[rowId(r)] = format(allCells(r, report.total, scope, vat)[m.id]); });
		cells.__total = format(allCells(report.total, report.total, scope, vat)[m.id]);
		return { id: m.id, cells };
	});
	// A transposed table holds numbers of different formats in one column: each cell carries its text.
	const transposedText = transposedRows.map(r => {
		const m = measures.find(x => x.id === r.id);
		const cells: DataTableRow['cells'] = { measure: r.cells.measure };
		Object.entries(r.cells).forEach(([k, v]) => {
			if (k === 'measure') return;
			cells[k] = v === null ? null : formatOf(v as number, m?.format ?? 'number', m?.decimals ?? 0, m?.signed ?? false);
		});
		return { ...r, cells };
	});
	const ly = lfl
		? ` Today like-for-like = today without ${missingNames.length === 1 ? 'the property' : `the ${missingNames.length} properties`} with no stay in the last-year period: ${missingNames.join(', ')}. vs STLY and vs LY actual compare it with last year.`
		: '';
	return <ChartCard title={`Room types · ${formatMonthRange(report === undefined ? period : { from: report.stayFrom.slice(0, 7), to: report.stayTo.slice(0, 7) })}`}
		period={report === undefined ? undefined : periodText(report)}
		state={state !== 'loading' && state !== 'error' && empty ? 'empty' : state} onRetry={onRetry}
		emptyLabel="No per-room-type data in these stay months."
		actions={<div className="flex flex-wrap items-center gap-2">{actions}</div>}
		caption={<>
			{`RN = sold room nights; Revenue = ${revenueName(scope, vat)} (${revenueContent(scope)}). STLY = the same stay dates last year, as they stood on ${formatDate(lastYear(asOf))}. `
				+ `LY actual = the same stay dates last year, as they stand today. STLY counts last year’s nights under the room type they have today. `
				+ `PU n = today − situation D-n. % = share of revenue. ADR includes the €0 nights (complimentary, house use)`
				+ `${occupancyWords(report).occupied === 'room nights' ? '' : ` and divides by the ${occupancyWords(report).occupied}`}. `
				+ `The TOTAL Res. counts each booking once: a booking with rooms of two types counts in both rows. An opened row lists its booking channels, then its boards (gray).${ly}`}
			{note !== null && <><br />{note}</>}
			{rulesNote !== null && <><br />{rulesNote}</>}
			{heldNote !== null && <><br />{heldNote}</>}
		</>}>
		{report !== undefined && !transposed && <div className="mb-4"><BarChart categories={['Revenue']} series={revenueBar(rows, report.total, scope, vat)} stacked max={1}
			format="percent" decimals={1} ariaLabel="Share of revenue by room type" /></div>}
		{report !== undefined && (transposed
			? <DataTable columns={transposedColumns} rows={transposedText} ariaLabel="Room types, transposed" framed={false} stickyHeader />
			: <DataTable columns={columns} columnGroups={groups} rows={tableRows} ariaLabel="Room types" framed={false} stickyHeader
				footer={{ id: 'total', cells: allCells(report.total, report.total, scope, vat) }} />)}
	</ChartCard>;
};

const lastYear = (iso: string) => `${Number(iso.slice(0, 4)) - 1}${iso.slice(4)}`;

const formatOf = (v: number, format: DataTableColumn['format'], decimals: number, signed: boolean): string => {
	if (format === 'money') return `${signed && v > 0 ? '+' : ''}€${v.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
	if (format === 'percent') return `${signed && v > 0 ? '+' : ''}${(v * 100).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}%`;
	return `${signed && v > 0 ? '+' : ''}${v.toLocaleString('en-US')}`;
};

type ByPropertyProps = {
	report: RoomTypeReport | undefined,
	period: MonthRange,
	properties: BiProperty[],
	companyName: string,
	scope: RevenueScope,
	vat: VatBasis,
	state: State,
	onRetry: () => void,
	actions?: React.ReactNode,
};

// By property (RQ-27, RQ-28): one row per property with its totals, its room types under it.
export const RoomTypeByPropertyCard: React.FunctionComponent<ByPropertyProps> = ({ report, period, properties, companyName, scope, vat, state, onRetry, actions }) => {
	const all = report === undefined ? null : revenue(today(report.total), scope, vat);
	const allRn = report === undefined ? 0 : today(report.total).roomNights;
	const words = occupancyWords(report);
	const rulesNote = report === undefined ? null : scopeRulesNote(report.reportRules, report.rulesDiffer, properties.length);
	const nameOf = (id: number) => { const p = properties.find(x => x.companyHotelId === id); return p === undefined ? String(id) : shortName(p, companyName); };
	const cells = (r: RoomTypeRow, base: { rev: number | null, rn: number }, occ: number | null) => {
		const rev = revenue(today(r), scope, vat);
		return {
			rn: today(r).roomNights, rev: rev === null ? null : Math.round(rev), adr: adr(rev, occupiedOf(today(r))),
			occ,
			revShare: rev === null || base.rev === null || base.rev === 0 ? null : rev / base.rev,
			rnShare: base.rn === 0 ? null : today(r).roomNights / base.rn, res: r.reservations,
		};
	};
	const rows: DataTableRow[] = report === undefined ? [] : [...report.properties]
		.sort((a, b) => (revenue(today(b.total), scope, vat) ?? 0) - (revenue(today(a.total), scope, vat) ?? 0))
		.map(p => {
			const own = { rev: revenue(today(p.total), scope, vat), rn: today(p.total).roomNights };
			const types = sortedRows(p.rows, scope, vat).filter(hasNightsToday);
			return {
				id: String(p.companyHotelId), note: `· ${types.filter(r => r.kind === 'ROOM_TYPE').length}`,
				cells: { name: nameOf(p.companyHotelId), ...cells(p.total, { rev: all, rn: allRn }, propertyOccupancy(p)) },
				children: types.map(r => ({ id: `${p.companyHotelId}-${r.key}`, muted: r.kind === 'NO_ROOM_TYPE',
					cells: { name: r.title, ...cells(r, own, typeOccupancy(r)) } })),
			};
		});
	return <ChartCard title={`Room types by property · ${formatMonthRange(period)}`} period={report === undefined ? undefined : periodText(report)}
		state={state !== 'loading' && state !== 'error' && report !== undefined && rows.length === 0 ? 'empty' : state} onRetry={onRetry}
		emptyLabel="No per-property data available."
		actions={<div className="flex flex-wrap items-center gap-2">{actions}</div>}
		caption={<>A property row holds the totals of the property; its room types open under it. Occ % = the {words.occupied} in the room that the hotel
			assigned ÷ (the room-days of the type in the period{words.rooms}), for the room types with room-days only; – without room-days. Rev share and RN share of a room type are
			of its property; of a property, of all properties.{rulesNote !== null && <><br />{rulesNote}</>}
			{heldBlocksNote(properties) !== null && <><br />{heldBlocksNote(properties)}</>}</>}>
		{report !== undefined && <DataTable ariaLabel="Room types by property" framed={false} stickyHeader rows={rows} defaultExpanded={rows.slice(0, 1).map(r => r.id)}
			columns={[{ id: 'name', label: 'Property / room type' }, { id: 'rn', label: 'RN', format: 'number' }, { id: 'rev', label: 'Revenue', format: 'money' },
				{ id: 'adr', label: 'ADR', format: 'money', decimals: 2, sortable: false }, { id: 'occ', label: 'Occ %', format: 'percent', decimals: 1, sortable: false },
				{ id: 'revShare', label: 'Rev share', format: 'percent', decimals: 1, sortable: false },
				{ id: 'rnShare', label: 'RN share', format: 'percent', decimals: 1, sortable: false }, { id: 'res', label: 'Res.', format: 'number' }]} />}
	</ChartCard>;
};
